/**
 * Flash Cosmetics - Custom JavaScript
 *
 * This file provides:
 * 1. Flash Cosmetics event system compatibility layer
 * 2. Event bridge between Flash EVENTS and Be Yours pubsub
 * 3. Custom functionality using data-flash-* attributes
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  // =========================================
  // Flash Cosmetics Event System
  // =========================================
  // Maps Flash Cosmetics custom events to Be Yours pubsub events
  // This allows any legacy code expecting Flash events to continue working

  const FLASH_EVENTS = {
    cartAdd: 'CART_ADD',
    cartOpen: 'CART_OPEN',
    cartUpdate: 'CART_UPDATE',
    slideChange: 'SLIDE_CHANGE'
  };

  // Map Flash events to Be Yours equivalents
  const EVENT_MAP = {
    'CART_ADD': 'cart-update',      // Be Yours uses cart-update for additions
    'CART_UPDATE': 'cart-update',   // Direct mapping
    'CART_OPEN': 'cartdrawer:opened', // Be Yours custom DOM event
    'SLIDE_CHANGE': 'flash:slide-change' // Custom Flash event (no Be Yours equivalent)
  };

  // Flash event subscribers
  let flashSubscribers = {};

  /**
   * Subscribe to a Flash Cosmetics event
   * @param {string} eventName - The Flash event name (e.g., 'CART_ADD')
   * @param {Function} callback - The callback function
   * @returns {Function} Unsubscribe function
   */
  function flashSubscribe(eventName, callback) {
    if (flashSubscribers[eventName] === undefined) {
      flashSubscribers[eventName] = [];
    }
    flashSubscribers[eventName] = [...flashSubscribers[eventName], callback];

    return function unsubscribe() {
      flashSubscribers[eventName] = flashSubscribers[eventName].filter(cb => cb !== callback);
    };
  }

  /**
   * Publish a Flash Cosmetics event
   * @param {string} eventName - The Flash event name
   * @param {Object} data - Event data
   */
  function flashPublish(eventName, data) {
    // Publish to Flash subscribers
    if (flashSubscribers[eventName]) {
      flashSubscribers[eventName].forEach(callback => {
        try {
          callback(data);
        } catch (e) {
          console.error('[Flash] Event callback error:', e);
        }
      });
    }

    // Also dispatch as DOM event for flexibility
    document.dispatchEvent(new CustomEvent(`flash:${eventName.toLowerCase()}`, {
      detail: data
    }));
  }

  // =========================================
  // Be Yours to Flash Event Bridge
  // =========================================
  // Listen to Be Yours events and republish as Flash events

  function initEventBridge() {
    // Bridge Be Yours cart-update to Flash CART_UPDATE
    if (typeof subscribe === 'function') {
      // Be Yours pubsub subscription
      subscribe('cart-update', (data) => {
        flashPublish(FLASH_EVENTS.cartUpdate, data);
        // Cart add is a subset of cart update
        if (data && data.source === 'product-form') {
          flashPublish(FLASH_EVENTS.cartAdd, data);
        }
      });
    }

    // Bridge DOM events
    document.addEventListener('cart:updated', (event) => {
      flashPublish(FLASH_EVENTS.cartUpdate, event.detail);
    });

    document.addEventListener('cartdrawer:opened', () => {
      flashPublish(FLASH_EVENTS.cartOpen, {});
    });

    // Bridge slider events from Be Yours scroll-snap-slider
    document.addEventListener('slider:change', (event) => {
      flashPublish(FLASH_EVENTS.slideChange, event.detail);
    });
  }

  // =========================================
  // Custom Flash Functionality
  // =========================================
  // Uses data-flash-* attributes for Flash-specific behavior

  /**
   * Initialize Flash menu drawer functionality
   * Handles data-flash-menu-btn, data-flash-menu-drawer, data-flash-menu-close-btn
   */
  function initFlashMenuDrawer() {
    const menuBtns = document.querySelectorAll('[data-flash-menu-btn]');
    const menuDrawer = document.querySelector('[data-flash-menu-drawer]');
    const menuCloseBtns = document.querySelectorAll('[data-flash-menu-close-btn]');

    if (!menuDrawer) return;

    menuBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        menuDrawer.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
      });
    });

    menuCloseBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        menuDrawer.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
      });
    });
  }

  /**
   * Initialize Flash cart drawer functionality
   * Handles data-flash-cart-drawer and related attributes
   */
  function initFlashCartDrawer() {
    const cartDrawer = document.querySelector('[data-flash-cart-drawer]');
    if (!cartDrawer) return;

    // Subscribe to cart updates to refresh cart contents
    flashSubscribe(FLASH_EVENTS.cartUpdate, () => {
      const cartContents = document.querySelector('[data-flash-cart-contents]');
      if (cartContents) {
        // Cart contents are managed by Be Yours mini-cart component
        // This hook allows for additional Flash-specific updates
      }
    });

    // Handle cart remove buttons with Flash prefix
    document.querySelectorAll('[data-flash-cart-remove-btn]').forEach(btn => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const lineItem = btn.closest('[data-flash-cart-line-item]');
        if (lineItem) {
          const index = lineItem.dataset.flashCartLineItem;
          // Trigger Be Yours cart removal
          const beYoursRemoveBtn = lineItem.querySelector('cart-remove-button');
          if (beYoursRemoveBtn) {
            beYoursRemoveBtn.click();
          }
        }
      });
    });
  }

  /**
   * Initialize Flash cart recommendations
   * Handles data-flash-cart-recommendations
   */
  function initFlashCartRecommendations() {
    const recommendationsContainer = document.querySelector('[data-flash-cart-recommendations]');
    if (!recommendationsContainer) return;

    // Recommendations are loaded by Be Yours cart-recommendations component
    // This hook allows for Flash-specific recommendation styling or behavior
  }

  /**
   * Initialize Flash product gallery pagination dots
   * Handles click events on .flash-gallery-dot buttons
   */
  function initFlashGalleryDots() {
    const dotsContainers = document.querySelectorAll('.flash-gallery-dots');

    dotsContainers.forEach(container => {
      const dots = container.querySelectorAll('.flash-gallery-dot');
      if (dots.length === 0) return;

      // Get the product media wrapper to find the slider
      const section = container.closest('.product');
      if (!section) return;

      const slider = section.querySelector('[is="scroll-snap-slider"], .product__media-list');
      const thumbnails = section.querySelectorAll('.thumbnail-list__item button, .thumbnail');

      // Click handler for dots
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          // Update active dot
          dots.forEach(d => {
            d.classList.remove('is-active');
            d.removeAttribute('aria-current');
          });
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');

          // Click corresponding thumbnail to change image
          if (thumbnails[index]) {
            thumbnails[index].click();
          } else if (slider) {
            // Fallback: scroll to the slide directly
            const slides = slider.querySelectorAll('.product__media-item, .slider__slide');
            if (slides[index]) {
              slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
          }
        });
      });

      // Sync dots when thumbnails are clicked
      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          dots.forEach(d => {
            d.classList.remove('is-active');
            d.removeAttribute('aria-current');
          });
          if (dots[index]) {
            dots[index].classList.add('is-active');
            dots[index].setAttribute('aria-current', 'true');
          }
        });
      });

      // Also sync on slider scroll (for swipe gestures)
      if (slider) {
        slider.addEventListener('scroll', debounce(() => {
          const slides = slider.querySelectorAll('.product__media-item, .slider__slide');
          if (slides.length === 0) return;

          const sliderRect = slider.getBoundingClientRect();
          const sliderCenter = sliderRect.left + sliderRect.width / 2;

          let closestSlide = 0;
          let closestDistance = Infinity;

          slides.forEach((slide, idx) => {
            const slideRect = slide.getBoundingClientRect();
            const slideCenter = slideRect.left + slideRect.width / 2;
            const distance = Math.abs(slideCenter - sliderCenter);

            if (distance < closestDistance) {
              closestDistance = distance;
              closestSlide = idx;
            }
          });

          dots.forEach(d => {
            d.classList.remove('is-active');
            d.removeAttribute('aria-current');
          });
          if (dots[closestSlide]) {
            dots[closestSlide].classList.add('is-active');
            dots[closestSlide].setAttribute('aria-current', 'true');
          }
        }, 100));
      }
    });
  }

  /**
   * Initialize Flash scroll-snap slider enhancements
   * Adds Flash events to Be Yours scroll-snap-slider elements
   */
  function initFlashSliders() {
    // Find all scroll-snap-sliders and add Flash event dispatch
    const sliders = document.querySelectorAll('[is="scroll-snap-slider"]');

    sliders.forEach(slider => {
      // Observer for slide changes
      let currentSlide = 0;

      slider.addEventListener('scroll', debounce(() => {
        const slides = slider.querySelectorAll('.slider__slide');
        if (slides.length === 0) return;

        const sliderRect = slider.getBoundingClientRect();
        const sliderCenter = sliderRect.left + sliderRect.width / 2;

        let closestSlide = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, index) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - sliderCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSlide = index;
          }
        });

        if (closestSlide !== currentSlide) {
          currentSlide = closestSlide;
          flashPublish(FLASH_EVENTS.slideChange, {
            slider: slider,
            currentSlide: currentSlide,
            totalSlides: slides.length
          });
        }
      }, 100));
    });
  }

  /**
   * Initialize Flash Products List slider dots
   * Handles click and scroll sync for mobile pagination dots
   */
  function initFlashProductsListDots() {
    const sections = document.querySelectorAll('.flash-products-list');

    sections.forEach(section => {
      const dotsContainer = section.querySelector('.flash-products-list__dots');
      if (!dotsContainer) return;

      const dots = dotsContainer.querySelectorAll('.flash-products-list__dot');
      const slider = section.querySelector('.flash-products-list__slides[is="scroll-snap-slider"]');
      if (!slider || dots.length === 0) return;

      const slides = slider.querySelectorAll('.slider__slide');

      // Click handler for dots
      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          // Update active dot
          dots.forEach(d => {
            d.classList.remove('is-active');
            d.removeAttribute('aria-current');
          });
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');

          // Scroll to corresponding slide
          if (slides[index]) {
            slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        });
      });

      // Sync dots on scroll
      slider.addEventListener('scroll', debounce(() => {
        if (slides.length === 0) return;

        const sliderRect = slider.getBoundingClientRect();
        const sliderCenter = sliderRect.left + sliderRect.width / 2;

        let closestSlide = 0;
        let closestDistance = Infinity;

        slides.forEach((slide, idx) => {
          const slideRect = slide.getBoundingClientRect();
          const slideCenter = slideRect.left + slideRect.width / 2;
          const distance = Math.abs(slideCenter - sliderCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSlide = idx;
          }
        });

        dots.forEach(d => {
          d.classList.remove('is-active');
          d.removeAttribute('aria-current');
        });
        if (dots[closestSlide]) {
          dots[closestSlide].classList.add('is-active');
          dots[closestSlide].setAttribute('aria-current', 'true');
        }
      }, 100));
    });
  }

  // =========================================
  // Utility Functions
  // =========================================

  /**
   * Debounce function
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  function debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // =========================================
  // Initialization
  // =========================================

  function init() {
    initEventBridge();
    initFlashMenuDrawer();
    initFlashCartDrawer();
    initFlashCartRecommendations();
    initFlashSliders();
    initFlashGalleryDots();
    initFlashProductsListDots();

    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('flash:ready'));
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', () => {
    initFlashMenuDrawer();
    initFlashCartDrawer();
    initFlashCartRecommendations();
    initFlashSliders();
    initFlashGalleryDots();
    initFlashProductsListDots();
  });

  // =========================================
  // Global API
  // =========================================
  // Expose Flash event system globally for external use

  window.Flash = window.Flash || {};
  window.Flash.EVENTS = FLASH_EVENTS;
  window.Flash.subscribe = flashSubscribe;
  window.Flash.publish = flashPublish;

})();
